import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "loader"]
  static values = { 
    url: String,
    page: { type: Number, default: 1 },
    loading: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: true }
  }

  connect() {    
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      threshold: 0.1,
      rootMargin: "100px"
    })
    
    if (this.hasLoaderTarget) {
      this.observer.observe(this.loaderTarget)
    }
    
    this.boundScrollHandler = this.handleScroll.bind(this)
    window.addEventListener('scroll', this.boundScrollHandler)
    
    // Check if loader is immediately visible (not enough content)
    setTimeout(() => {
      if (this.hasLoaderTarget && this.isElementInViewport(this.loaderTarget) && this.hasMoreValue) {
        this.loadMore()
      }
    }, 100)
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    window.removeEventListener('scroll', this.boundScrollHandler)
  }

  handleIntersection(entries) {
    entries.forEach(entry => {      
      if (entry.isIntersecting && this.hasMoreValue && !this.loadingValue) {
        this.loadMore()
      }
    })
  }

  handleScroll() {
    // Fallback for browsers that don't support IntersectionObserver
    if (!this.hasLoaderTarget || this.loadingValue || !this.hasMoreValue) return
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const windowHeight = window.innerHeight
    const documentHeight = document.documentElement.scrollHeight
    
    // Load more when user is 200px from bottom
    if (scrollTop + windowHeight >= documentHeight - 200) {
      this.loadMore()
    }
  }

  async loadMore() {
    if (this.loadingValue || !this.hasMoreValue) return
    
    this.loadingValue = true
    this.showLoader()
    
    const nextPage = this.pageValue + 1
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('page', nextPage)
    
    // Preserve existing filter parameters
    const currentParams = new URLSearchParams(window.location.search)
    for (const [key, value] of currentParams.entries()) {
      if (key !== 'page') {
        url.searchParams.set(key, value)
      }
    }
    
    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'Turbo-Frame': '_top'
        }
      })
      
      if (response.ok) {
        const responseText = await response.text()

        if (responseText && responseText.trim() !== '') {
          // Count items before processing
          const itemsBefore = this.containerTarget.children.length
          
          // Use Turbo's built-in stream processing
          const streamElement = document.createElement('div')
          streamElement.innerHTML = responseText
          
          // Process each turbo-stream element
          const turboStreams = streamElement.querySelectorAll('turbo-stream')
          turboStreams.forEach(stream => {
            // Let Turbo handle the stream processing
            document.body.appendChild(stream)
          })
          
          this.pageValue = nextPage
          
          // Check if new items were actually added after a brief delay
          setTimeout(() => {
            const itemsAfter = this.containerTarget.children.length
            
            if (itemsAfter === itemsBefore) {
              // No new items were added, assume no more pages
              this.hasMoreValue = false
              this.hideLoader()
            }
          }, 100)
        } else {
          // No content returned, no more pages
          this.hasMoreValue = false
          this.hideLoader()
        }
      } else {
        console.error('Failed to load more items:', response.statusText)
        this.hasMoreValue = false
        this.hideLoader()
      }
    } catch (error) {
      console.error('Error loading more items:', error)
      this.hasMoreValue = false
      this.hideLoader()
    } finally {
      this.loadingValue = false
    }
  }

  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.style.display = 'block'
    }
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.style.display = 'none'
    }
  }

  // Method to reset pagination (useful for filtering)
  reset() {
    this.pageValue = 1
    this.hasMoreValue = true
    this.loadingValue = false
    
    if (this.hasLoaderTarget) {
      this.observer.observe(this.loaderTarget)
      this.showLoader()
    }
  }

  // Helper method to check if element is in viewport
  isElementInViewport(element) {
    const rect = element.getBoundingClientRect()
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  // Target callbacks
  loaderTargetConnected() {
    // Re-observe when loader target is reconnected
    if (this.observer && this.hasMoreValue) {
      this.observer.observe(this.loaderTarget)
    }
  }

  loaderTargetDisconnected() {
    // If loader target is removed/replaced, stop infinite scroll
    this.hasMoreValue = false
  }
}
